// React
import PropTypes from "prop-types";
import { useContext, useEffect, useState } from "react";

// @mui material components
import { Card, CircularProgress, Fade, Grid, Icon, InputLabel, MenuItem, Modal, Select, Tooltip } from "@mui/material";

// Apollo
import { gql, useMutation, useQuery } from "@apollo/client";

// UI components
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";

// Context
import { MessageManager } from "context";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "60%", xxl: "50%" },
  overflowY: "auto",
  p: 3,
};

export default function ModalAddEvento({ open, handleClose, refetch, selectedEvento }) {
  const { handleSnackbar } = useContext(MessageManager);
  const [evento, setEvento] = useState();

  const handleChanges = (e) => {
    setEvento({
      ...evento,
      [e.target.name]: e.target.value,
    });
  };

  const { data: clientesData, loading: loadingClientes } = useQuery(
    gql`
      query getClientesEvento {
        clienteEventos {
          id
          nombre
          razonSocial
        }
      }
    `,
    { skip: !open }
  );

  const [saveEvento, { loading: loadingSave }] = useMutation(
    gql`
      mutation saveEvento($input: EventoInput!) {
        saveEvento(input: $input) {
          id
        }
      }
    `
  );

  const handleSave = () => {
    saveEvento({
      variables: {
        input: {
          id: evento?.id || selectedEvento?.id || undefined,
          idClienteEvento: evento?.idClienteEvento,
          fechaEvento: evento?.fechaEvento,
          estado: evento?.estado || undefined,
          nombreEvento: evento?.nombreEvento || undefined,
        },
      },
    })
      .then(() => {
        if (typeof refetch === "function") refetch();
        handleSnackbar("Evento guardado correctamente", "success");
        handleClose();
      })
      .catch(() => {
        handleSnackbar("Error al guardar el evento", "error");
      });
  };

  useEffect(() => {
    if (open) {
      if (selectedEvento) {
        setEvento({
          id: selectedEvento.id,
          idClienteEvento: selectedEvento?.clienteEvento?.id || "",
          fechaEvento: selectedEvento?.fecha || "",
          estado: selectedEvento?.estado || "Pendiente",
          nombreEvento: selectedEvento?.nombreEvento || "",
        });
      } else {
        setEvento({ estado: "Pendiente" });
      }
    } else {
      setEvento();
    }
  }, [open, selectedEvento]);

  const isSaveDisabled =
    loadingSave ||
    !evento?.idClienteEvento ||
    !evento?.fechaEvento ||
    !evento?.nombreEvento;

  return (
    <Modal aria-labelledby="modal-add-evento-title" open={open} onClose={handleClose} closeAfterTransition>
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox>
            <SoftBox display="flex" justifyContent="space-between">
              <SoftBox>
                <SoftTypography variant="h6">
                  {selectedEvento ? "Editar evento" : "Nuevo evento"}
                </SoftTypography>
              </SoftBox>
              <SoftBox display="flex" justifyContent="space-between">
                <SoftBox>
                  <Tooltip title="Cerrar" placement="top">
                    <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                      close
                    </Icon>
                  </Tooltip>
                </SoftBox>
              </SoftBox>
            </SoftBox>

            <SoftBox py={2}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <InputLabel htmlFor="nombreEvento">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Nombre del evento
                      <SoftTypography variant="caption" fontWeight="bold" color="primary">
                        &nbsp;*
                      </SoftTypography>
                    </SoftTypography>
                  </InputLabel>
                  <SoftInput
                    label="nombreEvento"
                    placeholder="Nombre del evento"
                    type="text"
                    name="nombreEvento"
                    value={evento?.nombreEvento || ""}
                    onChange={handleChanges}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <InputLabel htmlFor="idClienteEvento">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Cliente
                      <SoftTypography variant="caption" fontWeight="bold" color="primary">
                        &nbsp;*
                      </SoftTypography>
                    </SoftTypography>
                  </InputLabel>
                  <Select
                    name="idClienteEvento"
                    value={evento?.idClienteEvento || ""}
                    onChange={handleChanges}
                    disabled={loadingClientes}
                  >
                    <MenuItem value="" disabled>
                      {loadingClientes ? "Cargando clientes..." : "Seleccione un cliente"}
                    </MenuItem>
                    {clientesData?.clienteEventos?.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.nombre || c.razonSocial}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <InputLabel htmlFor="fechaEvento">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Fecha del evento
                      <SoftTypography variant="caption" fontWeight="bold" color="primary">
                        &nbsp;*
                      </SoftTypography>
                    </SoftTypography>
                  </InputLabel>
                  <SoftInput
                    label="fechaEvento"
                    placeholder="YYYY-MM-DD"
                    type="date"
                    name="fechaEvento"
                    value={evento?.fechaEvento || ""}
                    onChange={handleChanges}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <InputLabel htmlFor="estado">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Estado
                    </SoftTypography>
                  </InputLabel>
                  <Select name="estado" value={evento?.estado || ""} onChange={handleChanges}>
                    <MenuItem value="Pendiente">Pendiente</MenuItem>
                    <MenuItem value="Confirmado">Confirmado</MenuItem>
                    <MenuItem value="Cancelado">Cancelado</MenuItem>
                  </Select>
                </Grid>
              </Grid>
            </SoftBox>

            <SoftBox display={{ xs: "flex-row", sm: "flex" }} justifyContent="space-between" alignItems="center">
              <SoftBox></SoftBox>
              <SoftBox display={{ xs: "flex-row", sm: "flex" }} justifyContent="end" alignItems="center">
                <SoftBox mr={{ xs: 0, sm: 2 }} mt={{ xs: 2, sm: 0 }}>
                  <SoftButton color="primary" circular onClick={handleClose} fullWidth={{ xs: true, sm: false }}>
                    <Icon sx={{ fontWeight: "light" }}>cancel</Icon>
                    &nbsp;Cancelar
                  </SoftButton>
                </SoftBox>
                <SoftBox mt={{ xs: 2, sm: 0 }}>
                  <SoftButton
                    color="uproGreen"
                    circular
                    disabled={isSaveDisabled}
                    onClick={handleSave}
                    fullWidth={{ xs: true, sm: false }}
                  >
                    {loadingSave ? (
                      <CircularProgress size={15} color="inherit" />
                    ) : (
                      <Icon sx={{ fontWeight: "light" }}>save</Icon>
                    )}
                    &nbsp;Guardar
                  </SoftButton>
                </SoftBox>
              </SoftBox>
            </SoftBox>
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalAddEvento.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  refetch: PropTypes.func,
  selectedEvento: PropTypes.object,
};

